<template>
  <div
    class="project-harmonisation-container"
    :style="{ height: numberOfLines * 50 + 'px', 'min-height': '80px' }"
  >
    <b-loading v-model="isApproving" />
    <table
      v-if="!isApproving"
      :id="'project-harmonisation-table-' + projectAnnuel.id"
      class="project-harmonisation-table"
      :class="{ 'project-harmonisation-table-no-instructeur': evaluations.length == 0 }"
    >
      <!-- Non final evaluations -->
      <th class="empty"></th>
      <tr v-for="(evaluation, i) in sortedEvaluations" :key="evaluation.id">
        <td class="instructeur-cell">
          <p v-if="instructeurBeingDeletedId != evaluation.instructeur.id">
            <b-button
              icon-left="times"
              :title="$t('instructionPage.remove-instructeur')"
              size="is-small"
              class="remove-button"
              :disabled="
                loggedUser.profil === PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL ||
                (loggedUser.profil === PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL &&
                  loggedUser.id !== evaluation.instructeur.id)
              "
              @click="removeInstructeur(evaluation.instructeur)"
            />
            <b>{{ i + 1 }} - </b>{{ getInstructeurNameLabel(evaluation.instructeur) }}
          </p>
          <p v-else>
            {{ $t("instructionPage.labellisations.harmonisation.removing-instructeur") }}
          </p>
        </td>
        <td class="note-cell">
          <p>
            {{ evaluation.derouleNote }}
          </p>
        </td>

        <td class="note-cell">
          <p>
            {{ evaluation.adhesionNote }}
          </p>
        </td>
        <td class="note-cell">
          <p>
            {{ evaluation.integrationNote }}
          </p>
        </td>
        <td class="note-cell">
          <p>
            {{ evaluation.implicationNote }}
          </p>
        </td>
        <td class="comment-cell">
          <p>
            <LabellisationEvaluationCommentsPopup
              :project-annuel="projectAnnuel"
              :evaluation="evaluation"
              :is-final="false"
              @evaluation-updated="handleEvaluationUpdated"
              @comment-changed="handleCommentChanged"
            />
          </p>
        </td>
        <td class="mention-cell">
          <p class="mention">
            <span v-if="evaluation.mention">{{
              $t("instructionPage.labellisations.harmonisation.mention." + evaluation.mention)
            }}</span>
          </p>
        </td>
        <td
          v-if="i == 0"
          :rowspan="
            evaluations.length < maxInstructeursNumber
              ? evaluations.length + 2
              : evaluations.length + 1
          "
          class="actions-cell"
        >
          <div
            class="actions-container"
            :style="{ height: numberOfLines * 50 - 20 + 'px', 'min-height': '80px' }"
          >
            <b-button
              icon-left="eye"
              type="is-link is-outlined"
              :title="$t('consult-projet')"
              :label="$t('consult')"
              @click="consult($event)"
            >
            </b-button>
            <b-button
              icon-left="check"
              :disabled="!canApproveEvaluation"
              type="is-link is-outlined"
              :title="$t('instructionPage.labellisations.harmonisation.approve-evaluation')"
              :label="$t('instructionPage.labellisations.harmonisation.approve')"
              @click="approveEvaluation($event)"
            >
            </b-button>
          </div>
        </td>
      </tr>

      <!-- Add instructeur button -->
      <tr v-if="evaluations.length < maxInstructeursNumber">
        <td
          v-if="!isAddingInstructeur"
          :class="{
            'assign-instructeur-cell': evaluations.length > 0,
            'assign-instructeur-cell-with-no-evaluation-cell': evaluations.length == 0,
          }"
        >
          <b-dropdown
            aria-role="list"
            :disabled="disabledAssignedButton"
            @change="selectInstructeur"
          >
            <template #trigger>
              <b-button
                icon-right="plus"
                size="is-small"
                type="is-primary"
                :label="$t('instructionPage.assigner')"
              />
            </template>

            <div
              v-for="instructeur in allGraeInstructeursExceptAlreadyAssigned"
              :key="instructeur.id"
            >
              <b-dropdown-item
                aria-role="listitem"
                :value="instructeur"
                :disabled="loggedUser.profil === PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL"
              >
                {{ getInstructeurNameLabel(instructeur) }}
              </b-dropdown-item>
            </div>
          </b-dropdown>
        </td>
        <td
          v-else
          :class="{
            'assign-instructeur-cell': evaluations.length > 0,
            'assign-instructeur-cell-with-no-evaluation-cell': evaluations.length == 0,
          }"
        >
          {{ $t("instructionPage.labellisations.harmonisation.adding-instructeur") }}
        </td>
        <td v-for="i in 4" :key="'empty_' + projectAnnuel.id + i" class="note-cell"></td>
        <td class="comment-cell"></td>
        <td class="mention-cell"></td>
        <td v-if="evaluations.length == 0" class="actions-cell">
          <div class="actions-container">
            <b-button
              style="max-width: 110px; margin-left: 30px"
              icon-left="eye"
              type="is-link is-outlined"
              :title="$t('consult-projet')"
              :label="$t('consult')"
              @click="consult($event)"
            >
            </b-button>
          </div>
        </td>
      </tr>
      <!-- Final evaluation -->
      <tr v-if="evaluationFinale.id" class="final-eval-line">
        <td class="final-eval-cell">
          {{ $t("instructionPage.labellisations.harmonisation.note.final-eval") }}
        </td>
        <td class="note-cell">
          <b-select
            v-model="evaluationFinale.derouleNote"
            class="select-note"
            :disabled="!hasRightToModify"
            @update:model-value="finalEvalChanged(evaluationFinale)"
          >
            <option v-for="(option, i) in notes" :key="i" :value="option">{{ option }}</option>
          </b-select>
          <p></p>
        </td>

        <td class="note-cell">
          <b-select
            v-model="evaluationFinale.adhesionNote"
            class="select-note"
            :disabled="!hasRightToModify"
            @update:model-value="finalEvalChanged(evaluationFinale)"
          >
            <option v-for="(option, i) in notes" :key="i" :value="option">{{ option }}</option>
          </b-select>
        </td>
        <td class="note-cell">
          <b-select
            v-model="evaluationFinale.integrationNote"
            class="select-note"
            :disabled="!hasRightToModify"
            @update:model-value="finalEvalChanged(evaluationFinale)"
          >
            <option v-for="(option, i) in notes" :key="i" :value="option">{{ option }}</option>
          </b-select>
        </td>
        <td class="note-cell">
          <b-select
            v-model="evaluationFinale.implicationNote"
            class="select-note"
            :disabled="!hasRightToModify"
            @update:model-value="finalEvalChanged(evaluationFinale)"
          >
            <option v-for="(option, i) in notes" :key="i" :value="option">{{ option }}</option>
          </b-select>
        </td>
        <td class="comment-cell">
          <p>
            <LabellisationEvaluationCommentsPopup
              :project-annuel="projectAnnuel"
              :evaluation="evaluationFinale"
              :is-final="true"
              :read-only="!hasRightToModify"
              @evaluation-updated="handleEvaluationUpdated"
              @comment-changed="handleCommentChanged"
            />
          </p>
        </td>
        <td class="mention-cell">
          <p class="mention">
            <b-select
              v-model="evaluationFinale.mention"
              class="select-note"
              :disabled="!hasRightToModify"
              @update:model-value="finalEvalChanged(evaluationFinale)"
            >
              <option v-for="(option, i) in mentions" :key="i" :value="option">
                {{ $t("instructionPage.labellisations.harmonisation.mention." + option) }}
              </option>
            </b-select>
          </p>
        </td>
      </tr>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { BButton, BDropdown, BDropdownItem, BLoading, BSelect } from "buefy"
import RouterUtils from "@/router/RouterUtils"
import {
  EvaluationDTO,
  ProjetAnnuelForInstructionDTO,
  StatutDossier,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { UsersService } from "@/services/rest/users/UsersService"
import LabellisationEvaluationCommentsPopup from "./LabellisationEvaluationCommentsPopup.vue"

export default defineComponent({
  name: "LabellisationHarmonisationForProjectView",
  components: {
    BDropdownItem,
    LabellisationEvaluationCommentsPopup,
    BButton,
    BSelect,
    BDropdown,
    BLoading,
  },
  props: {
    projectAnnuel: {
      type: Object as () => ProjetAnnuelForInstructionDTO,
      required: true,
    },
    loggedUser: {
      type: Object as () => UtilisateurDTO,
      required: true,
    },
    allGraeInstructeurs: {
      type: Array as () => UtilisateurDTO[],
      required: true,
    },
    evaluations: {
      type: Array as () => EvaluationDTO[],
      required: true,
    },
  },

  emits: ["evaluations-changed", "reload-data"],
  data() {
    return {
      PROFIL_UTILISATEUR,
      instructionService: InstructionService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      usersService: UsersService.INSTANCE,
      allGraeInstructeursExceptAlreadyAssigned: [] as UtilisateurDTO[],
      evaluationFinale: new EvaluationDTO(),
      maxInstructeursNumber: 3,
      numberOfLines: 0,
      isAddingInstructeur: false,
      instructeurBeingDeletedId: "",
      canApproveEvaluation: false,
      isApproving: false,
      notes: [""],
      mentions: [] as StatutDossier[],
      hasRightToModify: true,
    }
  },
  computed: {
    disabledAssignedButton(): boolean {
      return (
        this.loggedUser.profil === PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL ||
        this.allGraeInstructeursExceptAlreadyAssigned.length === 0
      )
    },

    sortedEvaluations(): EvaluationDTO[] {
      return [...this.evaluations].sort(function (a, b) {
        const keyA = new Date(a.dateCreation),
          keyB = new Date(b.dateCreation)

        if (keyA < keyB) return -1
        if (keyA > keyB) return 1
        return 0
      })
    },
  },
  watch: {
    evaluations(): void {
      this.recomputedFinalEval()
      this.recomputeAvailableGraeInstructeurs()
    },
    allGraeInstructeurs(): void {
      this.recomputeAvailableGraeInstructeurs()
    },
  },
  mounted(): void {
    this.recomputedFinalEval()
    this.recomputeAvailableGraeInstructeurs()
    if (this.projectAnnuel.typeDossier == "DOSSIER_RENOUVELLEMENT_LIGHT") {
      this.mentions = ["ACCEPTE", "AVIS_PASSABLE", "REFUSE", "AUTOMATISE"]
      this.notes = [" ", "A", "B", "C", "D"]
    } else if (this.projectAnnuel.typeDossier == "DOSSIER_RENOUVELLEMENT") {
      this.mentions = ["ACCEPTE", "AVIS_PASSABLE", "REFUSE"]
      this.notes = ["A", "B", "C", "D"]
    } else {
      this.mentions = ["ACCEPTE", "AVIS_PASSABLE", "LABELLISATION_EN_DEVENIR", "REFUSE"]
      this.notes = ["A", "B", "C", "D"]
    }

    const alreadyInstructingIds = this.evaluations.map((e) => e.instructeur.id)
    this.hasRightToModify =
      this.loggedUser.profil !== PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL &&
      (this.loggedUser.profil !== PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL ||
        (this.loggedUser.profil === PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL &&
          alreadyInstructingIds.includes(this.loggedUser.id)))
    this.recomputeProjectCanApprove()
  },
  methods: {
    recomputedFinalEval(): void {
      if (this.evaluations.length > 0) {
        this.evaluationFinale = this.projectAnnuel.evaluationFinale
        // Compute final evaluation note & comment
        this.numberOfLines =
          this.evaluations.length +
          1 +
          (this.evaluations.length < this.maxInstructeursNumber ? 1 : 0)
      } else {
        this.evaluationFinale = new EvaluationDTO()
        // 1 line : that assign instructeur button
        this.numberOfLines = 1
      }
      if (!this.evaluationFinale.mention) {
        this.evaluationFinale.mention = this.evaluationFinale.mentionAutomatique
      }
      this.recomputeProjectCanApprove()
    },

    async selectInstructeur(instructeur: UtilisateurDTO): Promise<void> {
      this.isAddingInstructeur = true
      const projectWithUpdatedEvaluations = await this.instructionService.assignInstructeur(
        this.projectAnnuel.id,
        instructeur,
      )
      this.isAddingInstructeur = false
      this.$emit("evaluations-changed", projectWithUpdatedEvaluations)
    },

    async removeInstructeur(instructeur: UtilisateurDTO): Promise<void> {
      this.instructeurBeingDeletedId = instructeur.id ? instructeur.id : ""
      const projectWithUpdatedEvaluations = await this.instructionService.removeInstructeur(
        this.projectAnnuel.id,
        instructeur.id,
      )
      this.$emit("evaluations-changed", projectWithUpdatedEvaluations)
      this.instructeurBeingDeletedId = ""
    },

    recomputeAvailableGraeInstructeurs(): void {
      const alreadyInstructingIds = this.evaluations.map((e) => e.instructeur.id)
      this.allGraeInstructeursExceptAlreadyAssigned = this.allGraeInstructeurs.filter(
        (instructeur) => alreadyInstructingIds.indexOf(instructeur.id) == -1,
      )
    },

    getInstructeurNameLabel(instructeur: UtilisateurDTO): string {
      if (!instructeur || !instructeur.id) {
        return "as"
      }
      return this.loggedUser && instructeur.id === this.loggedUser.id
        ? this.$t("instructionPage.me").toString()
        : `${instructeur.nom} ${instructeur.prenom}`
    },

    consult(event: Event): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      const route = this.$router.resolve(
        RouterUtils.getRouteForProjectDetails(this.projectAnnuel.projetId, this.projectAnnuel.id),
      )
      window.open(route.href)
    },

    async approveEvaluation(event: Event): Promise<void> {
      this.isApproving = true
      event.stopPropagation()
      if (this.projectAnnuel?.id != null) {
        try {
          await this.instructionService.saveEvaluationFinale(
            this.projectAnnuel.id,
            this.evaluationFinale,
          )
          await this.instructionService.approuveEvaluationFinale(this.projectAnnuel.id)

          this.infoReportingService.success(
            this.$t("instructionPage.labellisations.harmonisation.envoyer-en-cloture").toString(),
          )
          this.isApproving = false
          this.$emit("reload-data")
        } catch (e: any) {
          this.infoReportingService.error(
            this.$t(
              "instructionPage.labellisations.harmonisation.envoyer-en-cloture-error",
            ).toString(),
            e as Error,
          )
          this.isApproving = false
        }
      }
    },

    async finalEvalChanged(evaluation: EvaluationDTO) {
      if (this.projectAnnuel?.id != null) {
        try {
          await this.instructionService.saveEvaluationFinale(this.projectAnnuel.id, evaluation)
          this.infoReportingService.success(
            this.$t(
              "instructionPage.labellisations.harmonisation.evaluation-finale-saved",
            ).toString(),
          )
          this.recomputeProjectCanApprove()
        } catch (_e) {
          this.infoReportingService.error(
            this.$t(
              "instructionPage.labellisations.harmonisation.evaluation-finale-save-ko",
            ).toString(),
            new Error(),
          )
        }
      }
    },

    recomputeProjectCanApprove(): void {
      if (this.projectAnnuel.typeDossier === "DOSSIER_RENOUVELLEMENT_LIGHT") {
        this.canApproveEvaluation =
          this.evaluationFinale.mention?.length > 0 &&
          this.evaluationFinale.commentaireInstructeur?.length > 0 &&
          this.hasRightToModify
      } else {
        this.canApproveEvaluation =
          this.evaluationFinale.adhesionNote?.length > 0 &&
          this.evaluationFinale.derouleNote?.length > 0 &&
          this.evaluationFinale.implicationNote?.length > 0 &&
          this.evaluationFinale.integrationNote?.length > 0 &&
          this.evaluationFinale.mention?.length > 0 &&
          this.evaluationFinale.adhesionCommentaire?.length > 0 &&
          this.evaluationFinale.derouleCommentaire?.length > 0 &&
          this.evaluationFinale.implicationCommentaire?.length > 0 &&
          this.evaluationFinale.integrationCommentaire?.length > 0 &&
          this.evaluationFinale.commentaireInstructeur?.length > 0 &&
          this.hasRightToModify
      }
    },

    handleEvaluationUpdated(updatedEvaluation: EvaluationDTO): void {
      const copyEvaluations = [...this.evaluations]
      // Met à jour l'évaluation dans la liste locale
      const index = copyEvaluations.findIndex((e) => e.id === updatedEvaluation.id)
      if (index !== -1) {
        copyEvaluations[index] = updatedEvaluation
      }
      this.$emit("evaluations-changed", { ...this.projectAnnuel, evaluations: copyEvaluations })

      // Si c'est l'évaluation finale, met à jour aussi
      if (updatedEvaluation.id === this.evaluationFinale.id) {
        this.evaluationFinale = updatedEvaluation
      }
      this.recomputeProjectCanApprove()
    },

    handleCommentChanged(): void {
      // Recalcule si le projet peut être approuvé après modification d'un commentaire
      this.recomputeProjectCanApprove()
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables.scss";

$note-width: 95px;
$instructeur-width: 180px;
$comment-width: 50px;
$mention-width: 110px;
$actions-width: 150px;
.project-harmonisation-container {
  .project-harmonisation-table {
    padding: 0 !important;
    width: $instructeur-width + 4 * $note-width + $comment-width + $mention-width + $actions-width +
      20;
    background-color: white;
    position: absolute;
    margin-top: -2px;

    tr {
      height: 50px;

      td {
        border-left: 1px solid $bookmarkcard-border-color;
        border-right: 1px solid $bookmarkcard-border-color;
        border-bottom: 1px solid $bookmarkcard-border-color;
        padding: 0.5em 0.7em;
      }

      &:first-child {
        td {
          border-top: 2px solid $instruction-harmonisation-final-eval-border-big;
        }
      }
    }
  }

  th.empty {
    display: none;
  }

  .project-harmonisation-table-no-instructeur {
    width: $instructeur-width + 4 * $note-width + $comment-width + $mention-width + $actions-width +
      120;
    height: 80px;

    tr {
      border-left: 1px solid $bookmarkcard-border-color;

      td {
        border-right: 0 solid $bookmarkcard-border-color !important;
        border-left: 0 solid $bookmarkcard-border-color !important;
        padding: 0.5em 4.1em;
      }
    }
  }
}

.instructeur-cell {
  width: $instructeur-width;

  p {
    width: $instructeur-width;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: "...";
  }
}

.assign-instructeur-cell {
  width: $instructeur-width + 30px;
}

.assign-instructeur-cell-with-no-evaluation-cell {
  width: $instructeur-width + 30px;
  padding: 0.5em 0.7em !important;
}

.final-eval-cell {
  text-transform: uppercase;
  width: $instructeur-width;
  font-weight: bolder;
}

.note-cell {
  width: $note-width;
  text-align: center;
  font-weight: bold;

  p {
    width: $note-width;
    text-align: center;
  }
}

.comment-cell {
  width: $comment-width;
  text-align: center;

  p {
    width: $comment-width;
    text-align: center;
  }
}

.mention-cell {
  width: $mention-width;
  text-align: center;

  p {
    width: $mention-width;
    text-align: center;
  }
}

.actions-cell {
  width: $actions-width;
  max-width: $actions-width;
  padding: 0.5em 0.7em !important;
  text-align: center;
  border-right: 0 solid black !important;
}

.remove-button {
  display: none !important;
  float: left;
}

.instructeur-cell:hover .remove-button {
  display: block !important;
}

.mention {
  text-transform: capitalize;
}

.final-eval-line {
  background-color: $instruction-harmonistation-final-eval-background;
}

.select-note {
  width: 100%;
  padding: 0 !important;

  .select {
    width: 100%;
    font-size: 14px !important;

    select {
      width: 100%;
      padding-right: 5px;
      font-size: 14px !important;

      option {
        font-size: 14px !important;
      }
    }
  }
}

.actions-container {
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  justify-content: center;
}
</style>
